// keycloak.js
import Keycloak from "keycloak-js";
import { settings } from "./config/settings";
import { clearSasUrlCache } from "./services/sasService";

const keycloakConfig = {
  url: settings.keycloakUrl,
  realm: settings.keycloakRealm,
  clientId: settings.keycloakClientId,
};

const keycloak = new Keycloak(keycloakConfig);
let initPromise = null;

/** Drop short-lived storage caches when the session ends (no full reload). */
const onSessionLost = () => {
  clearSasUrlCache();
};

/**
 * Keycloak 26.x: standard OIDC code flow + PKCE, login iframe off, silent
 * check-sso via silent-check-sso.html with fallback. Avoid removed/invalid
 * adapter options (e.g. promiseType, silent on init). User login uses
 * keycloak.login() so the authorization redirect is handled in the top window.
 */
const buildInitOptions = (overrides = {}) => ({
  flow: "standard",
  pkceMethod: "S256",
  checkLoginIframe: false,
  silentCheckSsoFallback: true,
  silentCheckSsoRedirectUri:
    globalThis.location.origin + "/silent-check-sso.html",
  ...overrides,
});

export const ensureKeycloakInitialized = (options = {}) => {
  if (!initPromise) {
    const { onLoad, ...rest } = options;
    const initOpts = buildInitOptions(
      onLoad !== undefined ? { onLoad, ...rest } : { ...rest },
    );

    initPromise = keycloak
      .init(initOpts)
      .then((authenticated) => {
        keycloak.initialized = true;
        if (authenticated) {
          sessionStorage.setItem("keycloakAuthenticated", "true");
          setInterval(() => {
            keycloak.updateToken(70).catch(() => {
              onSessionLost();
              keycloak.logout();
            });
          }, 60000);
        } else {
          onSessionLost();
          sessionStorage.removeItem("keycloakAuthenticated");
        }
        return authenticated;
      })
      .catch((error) => {
        onSessionLost();
        sessionStorage.removeItem("keycloakAuthenticated");
        console.error("Keycloak initialization failed", error);
        throw error;
      });
  }
  return initPromise;
};

export const initializeKeycloak = (onAuthenticatedCallback) => {
  ensureKeycloakInitialized({ onLoad: "check-sso" })
    .then((authenticated) => {
      if (!authenticated) {
        onSessionLost();
        sessionStorage.removeItem("keycloakAuthenticated");
      }
      onAuthenticatedCallback(authenticated);
    })
    .catch((error) => {
      onSessionLost();
      console.error("Keycloak session check failed", error);
      onAuthenticatedCallback(false);
    });
};

export const loginUser = () => {
  ensureKeycloakInitialized({ onLoad: "check-sso" })
    .then((authenticated) => {
      if (authenticated) {
        sessionStorage.setItem("keycloakAuthenticated", "true");
        return;
      }
      // Explicit redirect to Keycloak (KC 26+); interactive login completes the code flow
      keycloak.login().catch((error) => {
        console.error("Keycloak login redirect failed", error);
      });
    })
    .catch((error) => {
      console.error("Keycloak init before login failed", error);
      keycloak.login().catch((loginError) => {
        console.error("Keycloak login redirect failed", loginError);
      });
    });
};

export const isUserAuthenticated = () =>
  keycloak.authenticated &&
  sessionStorage.getItem("keycloakAuthenticated") === "true";

export const isTokenValid = () => keycloak.token && !keycloak.isTokenExpired();

export const logoutUser = () => {
  onSessionLost();
  // Clear all session storage
  sessionStorage.clear();
  // Clear any local storage if used
  localStorage.clear();
  // Call keycloak logout with proper redirect
  keycloak.logout({
    redirectUri: globalThis.location.origin,
  });
};

// Utility to get the user_name from the Keycloak token
export const getUsername = () => {
  if (!keycloak.tokenParsed) {
    console.warn("getUsername called but keycloak.tokenParsed is undefined");
  }
  const username = keycloak.tokenParsed?.preferred_username || null;
  console.log("getUsername called, returning:", username);
  return username;
};

// Utility to get the first name from the Keycloak token
export const getFirstName = () => {
  if (!keycloak.tokenParsed) {
    console.warn("getFirstName called but keycloak.tokenParsed is undefined");
  }
  const firstName = keycloak.tokenParsed?.given_name || null;
  console.log("getFirstName called, returning:", firstName);
  return firstName;
};

// Utility to get the last name from the Keycloak token
export const getLastName = () => {
  if (!keycloak.tokenParsed) {
    console.warn("getLastName called but keycloak.tokenParsed is undefined");
  }
  const lastName = keycloak.tokenParsed?.family_name || null;
  console.log("getLastName called, returning:", lastName);
  return lastName;
};

// Utility to get the email from the Keycloak token
export const getEmail = () => {
  if (!keycloak.tokenParsed) {
    console.warn("getEmail called but keycloak.tokenParsed is undefined");
  }
  const email = keycloak.tokenParsed?.email || null;
  console.log("getEmail called, returning:", email);
  return email;
};

export default keycloak;
